import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { ExtendedLead } from "@/components/ClientDetails/types";
import Step1InformacoesCliente from "@/components/Dimensionamento/Step1InformacoesCliente";
import Step2DimensionamentoSistema from "@/components/Dimensionamento/Step2DimensionamentoSistema";
import Step3EscolhaKit from "@/components/Dimensionamento/Step3EscolhaKit";
import Step4Precificacao from "@/components/Dimensionamento/Step4Precificacao";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { useAuthorization } from "@/contexts/AuthorizationContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { AlertCircle } from "lucide-react";
import {
  isProdutoLeadExcluidoDoDimensionamentoFv,
  PRODUTOS_LEAD_CARREGADOR,
  PRODUTOS_LEAD_ADEQUACOES_REFORMAS,
} from "@/constants/leadsProduto";

export default function Dimensionamento() {
  const isMobile = useIsMobile();
  const [leads, setLeads] = useState<ExtendedLead[]>([]);
  const [selectedLead, setSelectedLead] = useState<ExtendedLead | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [etapa4Cleared, setEtapa4Cleared] = useState(false);
  const [selectedDimensionamentoId, setSelectedDimensionamentoId] = useState<string | null>(null);
  const { hasRole, userProfile } = useAuthorization();
  const isVendedor = hasRole('vendedor' as any);
  const isParceiro = hasRole('parceiro' as any);
  /** Parceiro pai: pode ver leads da rede, sem editar */
  const isParceiroPai = isParceiro && (userProfile?.pode_criar_rede_parceiros === true);
  const isLeadFromRede = (lead: { parceiro_id?: string | null } | null) =>
    !!lead && isParceiroPai && lead.parceiro_id !== userProfile?.id;

  useEffect(() => {
    // Busca os leads da sua API (apenas com status "Lead")
    // Exclui leads de carregador/wallbox que não devem passar pelo dimensionamento solar
    // Exclui leads que têm propostas aceitas (já estão na aba contratos)
    const fetchLeads = async () => {
      try {
        const notProduto = encodeURIComponent(
          [...PRODUTOS_LEAD_CARREGADOR, ...PRODUTOS_LEAD_ADEQUACOES_REFORMAS].join(",")
        );
        const response = await fetchWithAuth(
          `/api/leads?status=Lead&not_produto=${notProduto}&exclude_propostas_aceitas=true&crm_funil=vendas`
        );
        if (!response.ok) {
          throw new Error('Erro ao buscar os leads');
        }
        const data = await response.json();
        // Filtro adicional no frontend para garantir que nenhum lead de carregador veicular apareça
        const filteredData = data.filter((lead: ExtendedLead) =>
          !isProdutoLeadExcluidoDoDimensionamentoFv(lead.produto)
        );
        setLeads(filteredData);
      } catch (error) {
        console.error("Falha ao carregar leads:", error);
      }
    };

    fetchLeads();
  }, []);

  const handleLeadChange = (leadId: string) => {
    const lead = leads.find(l => l.id === leadId) || null;
    setSelectedLead(lead);
  };

  const isReadOnly = (lead: ExtendedLead | null) => isLeadFromRede(lead);

  const nextStep = (dimensionamentoId?: string) => {
    if (dimensionamentoId) {
      setSelectedDimensionamentoId(dimensionamentoId);
    }
    // Parceiros podem acessar até a etapa 3; etapa 4 é concluída pelo admin
    const maxStep = isParceiro ? 3 : 4;
    setCurrentStep(prev => Math.min(prev + 1, maxStep));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const goToStep = (step: number) => {
    // Parceiros não podem acessar a etapa 4 (precificação — admin)
    if (isParceiro && step === 4) {
      return;
    }
    setCurrentStep(step);
  };

  const resetDimensionamento = () => {
    setCurrentStep(1);
    setSelectedLead(null);
    setEtapa4Cleared(false);
    setSelectedDimensionamentoId(null);
  };

  const finishProcess = async () => {
    if (!selectedLead?.id) {
      alert("Nenhum cliente selecionado para finalizar o dimensionamento.");
      return;
    }

    if (!selectedDimensionamentoId) {
      alert("Nenhum dimensionamento selecionado para finalizar.");
      return;
    }

    try {
      // Para vendedores e parceiros: NÃO marcar como finalizado
      if (isVendedor || isParceiro) {
        resetDimensionamento();
        return;
      }

      // Para admins: Marcar a proposta como finalizada no banco de dados
      const { supabase } = await import('@/integrations/supabase/client');
      
      const { error } = await supabase
        .from('propostas_clientes')
        .update({ dimensionamento_finalizado: true })
        .eq('lead_id', selectedLead.id)
        .eq('dimensionamento_id', selectedDimensionamentoId);

      if (error) {
        console.error('Erro ao marcar dimensionamento como finalizado:', error);
        alert("Erro ao finalizar o dimensionamento. Tente novamente.");
        return;
      }

      alert("Processo de dimensionamento finalizado com sucesso! O cliente agora aparecerá na lista de propostas.");
      resetDimensionamento();
    } catch (error) {
      console.error('Erro ao finalizar dimensionamento:', error);
      alert("Erro ao finalizar o dimensionamento. Tente novamente.");
    }
  };

  const handleKitsChanged = () => {
    setEtapa4Cleared(true);
  };

  const handleEtapa4DataSaved = () => {
    setEtapa4Cleared(false);
  };

  return (
    <div 
      className={`space-y-6 ${isMobile ? 'p-4' : 'p-6'} overflow-x-hidden max-w-full w-full`}
      style={{ 
        maxWidth: isMobile ? 'calc(100vw - 2rem)' : '100%', 
        width: '100%', 
        boxSizing: 'border-box', 
        overflowX: 'hidden' 
      }}
    >
      <h1 className="text-3xl font-bold text-foreground">Dimensionamento</h1>
      
      {/* Step indicator */}
      <Card className="w-full overflow-x-hidden min-w-0">
        <CardHeader>
          <CardTitle>Processo de Dimensionamento</CardTitle>
        </CardHeader>
        <CardContent className="w-full min-w-0">
          <p className="text-muted-foreground mb-4">
            {isParceiro
              ? "Complete as etapas 1 a 3. Após a escolha do kit, o dimensionamento será enviado para precificação pelo administrador."
              : "O dimensionamento será feito em 4 etapas. Siga os passos abaixo:"}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <Button 
              variant={currentStep === 1 ? "default" : "outline"} 
              onClick={() => goToStep(1)}
              className="h-auto py-3 px-3 text-center whitespace-normal"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-2 lg:space-y-0 space-y-1">
                <span className="text-sm font-medium">
                  Etapa 1
                </span>
                <span className="text-sm">
                  Informações do cliente
                </span>
              </div>
            </Button>
            <Button 
              variant={currentStep === 2 ? "default" : "outline"} 
              onClick={() => goToStep(2)}
              className="h-auto py-3 px-3 text-center whitespace-normal"
              disabled={currentStep < 2}
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-2 lg:space-y-0 space-y-1">
                <span className="text-sm font-medium">
                  Etapa 2
                </span>
                <span className="text-sm">
                  Dimensionamento do Sistema
                </span>
              </div>
            </Button>
            <Button 
              variant={currentStep === 3 ? "default" : "outline"} 
              onClick={() => goToStep(3)}
              className="h-auto py-3 px-3 text-center whitespace-normal"
              disabled={currentStep < 3}
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-2 lg:space-y-0 space-y-1">
                <span className="text-sm font-medium">
                  Etapa 3
                </span>
                <span className="text-sm">
                  Escolha do Kit
                </span>
              </div>
            </Button>
            <Button 
              variant={currentStep === 4 ? "default" : "outline"} 
              onClick={() => goToStep(4)}
              className="h-auto py-3 px-3 text-center whitespace-normal"
              disabled={currentStep < 4 || isParceiro}
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-2 lg:space-y-0 space-y-1">
                <span className="text-sm font-medium">
                  Etapa 4
                </span>
                <span className="text-sm">
                  Precificação
                </span>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Current step content */}
      <div 
        className="w-full min-w-0 max-w-full overflow-x-hidden"
        style={{ 
          maxWidth: isMobile ? 'calc(100vw - 2rem)' : '100%', 
          width: '100%', 
          boxSizing: 'border-box', 
          overflowX: 'hidden' 
        }}
      >
        {currentStep === 1 && (
          <Step1InformacoesCliente 
            leads={leads} 
            selectedLead={selectedLead} 
            onSelectLead={handleLeadChange}
            onNext={nextStep}
            readOnly={isReadOnly(selectedLead)}
            allowSelectForParceiro={isParceiro}
          />
        )}
        {currentStep === 2 && (
          <Step2DimensionamentoSistema 
            onBack={prevStep}
            onNext={nextStep}
            selectedLead={selectedLead}
            dimensionamentoId={selectedDimensionamentoId}
            readOnly={isReadOnly(selectedLead)}
          />
        )}
        {currentStep === 3 && (
          <Step3EscolhaKit 
            onBack={prevStep}
            onNext={nextStep}
            onFinish={resetDimensionamento}
            selectedLead={selectedLead}
            onKitsChanged={handleKitsChanged}
            dimensionamentoId={selectedDimensionamentoId}
            readOnly={isReadOnly(selectedLead)}
            isParceiro={isParceiro && !isLeadFromRede(selectedLead)}
          />
        )}
        {currentStep === 4 && (
          isParceiro ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Acesso Negado</AlertTitle>
              <AlertDescription>
                A etapa 4 (Precificação) é concluída pelo administrador após o envio do dimensionamento na etapa 3.
              </AlertDescription>
            </Alert>
          ) : (
            <Step4Precificacao 
              onBack={prevStep}
              onFinish={finishProcess}
              selectedLead={selectedLead}
              etapa4Cleared={etapa4Cleared}
              onEtapa4DataSaved={handleEtapa4DataSaved}
              dimensionamentoId={selectedDimensionamentoId}
            />
          )
        )}
      </div>
    </div>
  );
}
